import React from 'react';
import { motion } from 'framer-motion';
import { StatsSection } from '../components/sections/StatsSection';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/bkfmz2dq_ChatGPT%20Image%20Feb%208%2C%202026%2C%2005_38_07%20PM.png";

export const AboutPage = () => {
  return (
    <main data-testid="about-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1564842505181-8862a3b9b173?w=1920&q=80"
            alt="Workshop"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-leather/80"></div>
        </div>
        <div className="relative text-center z-10 px-4">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-gold font-body text-sm uppercase tracking-widest"
          >
            Our Story
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4"
          >
            About Last Upholstery
          </motion.h1>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-cream">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={LOGO_URL} 
                alt="Last Upholstery Logo" 
                className="w-48 mb-8"
              />
              <h2 className="font-heading text-3xl md:text-4xl text-leather mb-6">
                Crafting Comfort Since 2015
              </h2>
              <p className="text-leather/70 font-body leading-relaxed mb-6">
                Last Upholstery began with a simple vision: to provide South African homes 
                with beautifully crafted, comfortable furniture at fair prices. What started 
                as a small workshop has grown into a trusted name in couch manufacturing 
                and reupholstery.
              </p>
              <p className="text-leather/70 font-body leading-relaxed mb-6">
                Our team of skilled craftsmen brings together traditional techniques and 
                modern design sensibilities to create pieces that are both functional and 
                beautiful. Every couch that leaves our workshop is a testament to our 
                commitment to quality.
              </p>
              <p className="text-leather/70 font-body leading-relaxed">
                Whether you're looking for a custom-built piece, a ready-made couch from 
                our collection, or need to breathe new life into your existing furniture, 
                we're here to help transform your space.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/44vd5t34_lst7.jpeg"
                alt="Our Work"
                className="w-full h-48 object-cover rounded-xl shadow-card"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/3s6svqv6_lst8.jpeg"
                alt="Our Work"
                className="w-full h-48 object-cover rounded-xl shadow-card mt-8"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/wfnsvrjo_lst9.jpeg"
                alt="Our Work"
                className="w-full h-48 object-cover rounded-xl shadow-card"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/c93i9oc7_WhatsApp%20Image%202026-02-08%20at%2016.14.58.jpeg"
                alt="Our Work"
                className="w-full h-48 object-cover rounded-xl shadow-card mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-gold font-body text-sm uppercase tracking-widest">Why Choose Us</span>
            <h2 className="font-heading text-3xl md:text-4xl text-leather mt-2">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'fa-gem', title: 'Quality', desc: 'Premium materials and expert craftsmanship in every piece' },
              { icon: 'fa-handshake', title: 'Integrity', desc: 'Honest pricing and transparent communication always' },
              { icon: 'fa-heart', title: 'Passion', desc: 'Love for furniture and dedication to customer satisfaction' },
              { icon: 'fa-leaf', title: 'Sustainability', desc: 'Eco-friendly practices and responsible sourcing' }
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 rounded-2xl bg-secondary/30 hover:bg-secondary/50 transition-colors"
              >
                <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
                  <i className={`fas ${value.icon} text-gold text-2xl`}></i>
                </div>
                <h3 className="font-heading text-xl text-leather font-semibold mb-3">
                  {value.title}
                </h3>
                <p className="text-leather/60 font-body text-sm">
                  {value.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <StatsSection />
    </main>
  );
};

export default AboutPage;
